import {
  EnterpriseDepartment,
  EnterpriseDepartmentUserType,
  Job,
  JobScreeningInfo,
  MailReplacedTemplateRequest,
  MailTemplateResponse,
  ProgressBulkSettingInterviewRequest,
  ProgressScreeningRequestStatus,
  SCREENING_INFO_REQUEST_DEADLINE_OPTIONS,
  StudentDetail,
  StudentMailBulkSendRequest,
  User
} from "@agent-ds/shared/interfaces";
import {
  AuthService,
  CompanyApiService,
  JobApiService,
  MailApiService,
  ProgressApiService,
  StudentApiService,
  UserApiService
} from "@agent-ds/shared/services";
import {
  formatTemplateMultilineValue,
  getValueFromObject
} from "@agent-ds/shared/util/util";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { forkJoin, of, Subscription } from "rxjs";
import { catchError, concatMap, map } from "rxjs/operators";
import { CacheInterceptor } from "@agent-ds/shared/interceptors/cache.interceptor";
import { PopupControlComponent } from "@agent-ds/shared/components/popup-control/popup-control.component";

import { DialogConfig } from "../../../shared/components/dialog/dialog-config";
import { DialogRef } from "../../../shared/components/dialog/dialog-ref";
import {
  ADJUST_INTERVIEW_SYSTEM_NAMES,
  EMAIL_NOTIFICATION_RECIPIENT_TYPE
} from "@agent-ds/shared/constants/mail";
import {
  PROGRESS_SUB_SCREENING_STATUS_CODE_LABEL_MAPPING,
  ProgressScreeningRequestStatusRequestNoEnum,
  ProgressScreeningRequestStatusRequestStatusEnum,
} from "@agent-ds/shared/enums";

import * as moment from "moment";

export type EmailNotificationRecipientType = typeof EMAIL_NOTIFICATION_RECIPIENT_TYPE[keyof typeof EMAIL_NOTIFICATION_RECIPIENT_TYPE];

@Component({
  selector: "ag-progress-sub-screening-schedule-adjust-dialog",
  templateUrl: "./progress-sub-screening-schedule-adjust-dialog.component.html",
  styleUrls: ["./progress-sub-screening-schedule-adjust-dialog.component.scss"]
})
export class ProgressSubScreeningScheduleAdjustDialogComponent
  implements OnInit, OnDestroy {
  model: {
    to: string;
    toEmails: string[];
    cc: string[];
    cc1: string;
    cc2: string;
    cc3: string;
    enterpriseName: string;
    jobPosition: string;
    selectionName: string;
    request1?: boolean;
    request2?: boolean;
    request3?: boolean;
    request1Deadline?: string;
    request2Deadline?: string;
    request3Deadline?: string;
    subject?: string;
    body?: string;
  };
  scheduleRadio = 0;
  scheduleText = "";
  schedulePreviewList: string[] = [];
  message = "";
  screeningInfo: JobScreeningInfo | null = null;
  screeningInfoRequests: any[];
  inConfirm = false;
  isTemplateLoadInProgress = false;
  isSendInProgress = false;
  private mailTemplateSubscription: Subscription | null = null;
  private sendSubscription: Subscription | null = null;
  confirmSubscription: Subscription;
  confirmedMailTemplateTypeId: number;
  notifyRadio: EmailNotificationRecipientType =
    EMAIL_NOTIFICATION_RECIPIENT_TYPE.BIC;
  atsAddressChecked = false;
  atsAddress = "";
  private enterpriseDepartmentId?: number;
  private enterpriseDepartment: EnterpriseDepartment;
  private raAndAssistantUsers: User[] = [];
  contacts: { name: string; email: string; id: number }[] = [];
  contactSelections: { to: boolean[]; cc: boolean[] } = {
    to: [],
    cc: []
  };
  supplierName: string = "";
  jobScreeningInfo?: JobScreeningInfo;
  private possibleDate: string = "";

  constructor(
    public dialog: DialogRef,
    public config: DialogConfig,
    private studentApiService: StudentApiService,
    private jobApiService: JobApiService,
    private mailApiService: MailApiService,
    private authService: AuthService,
    private progressApiService: ProgressApiService,
    private userService: UserApiService,
    private companyApiService: CompanyApiService
  ) {}

  ngOnInit() {
    this.confirmSubscription = this.dialog.confirmed.subscribe(() =>
      this.inConfirm ? this.send() : this.getMailTemplate()
    );
    this.load();
  }

  ngOnDestroy() {
    this.cancelMailTemplate();
    this.cancelSend();

    if (this.confirmSubscription) {
      this.confirmSubscription.unsubscribe();
    }
  }

  isValidForConfirm(): boolean {
    if (!this.model || !this.model.toEmails) {
      return false;
    }

    if (this.scheduleRadio !== 1) {
      return true;
    }

    return this.hasManualNotificationRecipient();
  }

  private hasManualNotificationRecipient(): boolean {
    const hasToSelection = this.contactSelections.to.some(selected => selected);

    if (this.notifyRadio === EMAIL_NOTIFICATION_RECIPIENT_TYPE.ENTERPRISE) {
      if (!this.atsAddressChecked) {
        return hasToSelection;
      }

      if (this.atsAddressChecked && !this.isEmail(this.atsAddress)) {
        return false;
      }

      const hasAtsValid =
        this.atsAddressChecked && this.isEmail(this.atsAddress);

      return hasAtsValid || hasToSelection;
    }

    return hasToSelection;
  }

  clearScheduleText(): void {
    this.scheduleText = "";
  }

  public onScheduleRadioChange(): void {
    this.clearScheduleText();

    if (this.scheduleRadio === 1 && this.contacts.length === 0) {
      this.loadForStudentSpecified();
    }
  }

  public loadForStudentSpecified(): void {
    this.companyApiService
      .getDepartments(this.config.data.enterpriseId)
      .subscribe(department => {
        this.enterpriseDepartment = department.enterpriseDepartments.find(
          d => d.id === this.enterpriseDepartmentId
        );

        this.supplierName += ` ${this.enterpriseDepartment.dynamicData.name}`;
        this.initializeBicContactSelections();
      });
  }

  public onNotifyRadioChange(value: EmailNotificationRecipientType): void {
    this.notifyRadio = value;
    this.contactSelections.to = [];
    this.contactSelections.cc = [];

    // UI 切替時はデフォルトチェックに戻す
    if (this.notifyRadio === EMAIL_NOTIFICATION_RECIPIENT_TYPE.ENTERPRISE) {
      this.initializeEnterpriseContactSelections();
    } else {
      this.initializeBicContactSelections();
    }
  }

  public initializeBicContactSelections(): void {
    this.contacts = this.raAndAssistantUsers
      .filter(u => u.name && u.email)
      .map(u => ({ name: u.name, email: u.email, id: u.id }));

    const len = this.contacts.length;
    if (len === 0) {
      this.contactSelections.to = [];
      this.contactSelections.cc = [];
      return;
    }

    this.contactSelections.to = new Array(len).fill(true);
    this.contactSelections.cc = new Array(len).fill(false);
  }

  private initializeEnterpriseContactSelections(): void {
    this.contacts = [
      this.enterpriseDepartment.dynamicData.contact1,
      this.enterpriseDepartment.dynamicData.contact2,
      this.enterpriseDepartment.dynamicData.contact3,
      this.enterpriseDepartment.dynamicData.contact4,
      this.enterpriseDepartment.dynamicData.contact5
    ].filter(c => c && c.name && c.email && !c.emailAvailable);

    const len = this.contacts.length;
    if (len === 0) {
      this.contactSelections.to = [];
      this.contactSelections.cc = [];
      return;
    }

    this.contactSelections.to = new Array(len).fill(false);
    this.contactSelections.cc = new Array(len).fill(false);

    // To: first only
    this.contactSelections.to[0] = true;
    // CC: all except first
    for (let i = 1; i < len; i++) {
      this.contactSelections.cc[i] = true;
    }
  }

  generatePreview(): void {
    this.schedulePreviewList = this.scheduleText
      .split("\n")
      .filter(v => v && v.trim())
      .map(v => v.trim());
  }

  private load(): void {
    const jobId = this.config.data.jobId;
    const studentIds = this.config.data.studentIds;
    const progressSubScreeningStatus = this.config.data.progressSubScreeningStatus;
    const jobScreeningInfo: JobScreeningInfo = this.config.data.jobScreeningInfo;
    this.jobScreeningInfo = jobScreeningInfo;

    if (
      jobId == null ||
      studentIds == null ||
      !progressSubScreeningStatus ||
      !jobScreeningInfo
    ) {
      return;
    }

    forkJoin(
      studentIds.map(studentId => this.studentApiService.getDetail(studentId))
    ).subscribe((students: StudentDetail[]) => {
      this.jobApiService.getDetail(jobId).subscribe((job: Job) => {
        this.enterpriseDepartmentId = job.enterpriseDepartmentId;
        this.loadForStudentSpecified();
        this.userService.getAll().subscribe(users => {
          const raAsUserObjs = job.enterpriseDepartmentUsers.filter(
            u => u.type === EnterpriseDepartmentUserType.アシ
          );
          const raAsUserObjsIds = raAsUserObjs.map(r => r.userId);
          const raAsUsers = users.filter(u => raAsUserObjsIds.includes(u.id));

          // 通知先が社内宛のメールアドレスを設定
          const raRelatedUserTypes = [
            EnterpriseDepartmentUserType.アシ,
            EnterpriseDepartmentUserType.RA
          ];
          const raAndAssistantUserIds = job.enterpriseDepartmentUsers
            .filter(u => raRelatedUserTypes.includes(u.type))
            .map(u => u.userId);
          this.raAndAssistantUsers = users.filter(u =>
            raAndAssistantUserIds.includes(u.id)
          );
          this.supplierName = `${job.frontEnterpriseId} ${job.enterpriseName}${job.enterpriseNo}`;

          const raMails = raAsUsers.map(r => r.email).filter(e => e);
          this.model = {
            to: students
              .map(student => {
                const name =
                  (student.dynamicData.lastName || "") +
                  (student.dynamicData.firstName || "");
                const mail = getValueFromObject(
                  student,
                  "dynamicData.emailMain"
                );
                return mail && !mail.emailAvailable && mail.email
                  ? `${name} <${mail.email}>`
                  : `【送信不可】${name}`;
              })
              .filter(v => v && v.trim())
              .join(", "),
            toEmails: students
              .map(student => {
                const mail = getValueFromObject(
                  student,
                  "dynamicData.emailMain"
                );
                return mail && mail.email ? mail.email : null;
              })
              .filter(v => v && v.trim()),
            cc: [
              ...(this.config.data.title
                ? students
                    .map(student => {
                      const mail = this.config.data.title
                        ? getValueFromObject(student, "dynamicData.emailSub")
                        : null;
                      return mail && mail.email ? mail.email : null;
                    })
                    .filter(v => v && v.trim())
                : []),
              ...(this.config.data.title && this.config.data.progressType === 0
                ? students
                    .map(student => {
                      const studentUserObj = student.studentUsers.find(
                        u => u.type === EnterpriseDepartmentUserType.CA
                      );
                      const studentUser = studentUserObj
                        ? users.find(u => u.id === studentUserObj.userId)
                        : null;
                      return studentUser ? studentUser.email : null;
                    })
                    .filter(v => v && v.trim())
                : []),
              ...(raMails && raMails.length ? raMails : [])
            ],
            cc1: !this.config.data.title
              ? "※E-mailサブにも送信"
              : students
                  .map(student => {
                    const name =
                      (student.dynamicData.lastName || "") +
                      (student.dynamicData.firstName || "");
                    const mail = this.config.data.title
                      ? getValueFromObject(student, "dynamicData.emailSub")
                      : null;
                    return mail && !mail.emailAvailable && mail.email
                      ? `${name} <${mail.email}>`
                      : name;
                  })
                  .filter(v => v && v.trim())
                  .join(", "),
            cc2: !this.config.data.title
              ? "※求職者担当にも送信"
              : this.config.data.progressType === 0
              ? students
                  .map(student => {
                    const studentUserObj = student.studentUsers.find(
                      u => u.type === EnterpriseDepartmentUserType.CA
                    );
                    const studentUser = studentUserObj
                      ? users.find(u => u.id === studentUserObj.userId)
                      : null;
                    return studentUser
                      ? `${studentUser.name} <${studentUser.email}>`
                      : name;
                  })
                  .filter(v => v && v.trim())
                  .join(", ")
              : "",
            cc3:
              raAsUsers && raAsUsers.length
                ? raAsUsers.map(r => `${r.name} <${r.email}>`).join(",")
                : "",
            enterpriseName: job.enterpriseName,
            jobPosition: job.dynamicData.position,
            selectionName: jobScreeningInfo.type,
          };
          this.screeningInfo = jobScreeningInfo || null;

          if (this.screeningInfo) {
            if (this.screeningInfo.request1) {
              this.model.request1 = true;
            }
            if (this.screeningInfo.request2) {
              this.model.request2 = true;
            }
            if (this.screeningInfo.request3) {
              this.model.request3 = true;
            }
            if (this.screeningInfo.request1Deadline) {
              this.model.request1Deadline = this.screeningInfo.request1Deadline;
            }
            if (this.screeningInfo.request2Deadline) {
              this.model.request2Deadline = this.screeningInfo.request2Deadline;
            }
            if (this.screeningInfo.request3Deadline) {
              this.model.request3Deadline = this.screeningInfo.request3Deadline;
            }
          }
        });
      });
    });
  }

  getMailTemplate(): void {
    if (!this.isValidForConfirm() || this.isTemplateLoadInProgress) {
      return;
    }

    this.model.subject = null;
    this.model.body = null;
    this.isTemplateLoadInProgress = true;

    const progressIds: number[] =
      (this.config.data && Array.isArray(this.config.data.progressIds) && this.config.data.progressIds) ||
      (this.config.data && this.config.data.progress && this.config.data.progress.id != null ? [this.config.data.progress.id] : []) ||
      (this.config.data && this.config.data.progressSubScreeningStatus && this.config.data.progressSubScreeningStatus.progressId != null
        ? [this.config.data.progressSubScreeningStatus.progressId]
        : []);
    const progressIdForStatus: number | null = progressIds.length === 1 ? progressIds[0] : null;

    const progressSubScreeningStatusId: number | null =
      this.config.data && this.config.data.progressSubScreeningStatus && this.config.data.progressSubScreeningStatus.id != null
        ? this.config.data.progressSubScreeningStatus.id
        : null;

    this.mailTemplateSubscription = this.mailApiService
      .getMailTemplates()
      .pipe(
        map((templates: MailTemplateResponse[]) => {
          const systemName =
            this.scheduleRadio === 0
              ? ADJUST_INTERVIEW_SYSTEM_NAMES.STUDENT_ADJUST_SUB_SCREENING_COMPANY
              : ADJUST_INTERVIEW_SYSTEM_NAMES.STUDENT_ADJUST_SUB_SCREENING_STUDENT_ONLY;
          return templates.find(
            t => t.systemName === systemName
          );
        }),
        concatMap((template: MailTemplateResponse) => {
          if (!progressIdForStatus) {
            return of({ template, statuses: [] as ProgressScreeningRequestStatus[] });
          }
          return this.progressApiService.getProgressScreeningRequestStatusList(progressIdForStatus).pipe(
            catchError(() => of([] as ProgressScreeningRequestStatus[])),
            map((statuses: ProgressScreeningRequestStatus[]) => ({ template, statuses }))
          );
        }),
        concatMap(({ template, statuses }: { template: MailTemplateResponse; statuses: ProgressScreeningRequestStatus[] }) => {
          const templateObj: MailReplacedTemplateRequest = {
            from: this.authService.loginUser.email,
            subject: template.subject,
            text: template.body,
            to: this.model.toEmails,
            jobId: this.config.data.jobId,
            progress: this.config.data.progress,
          };
          if (
            this.config.data.studentIds &&
            this.config.data.studentIds.length === 1
          ) {
            templateObj.studentId = this.config.data.studentIds[0];
          }

          const correspondence = this.generateCorrespondenceText();

          templateObj.additionalReplacements =
            templateObj.additionalReplacements || [];

          templateObj.additionalReplacements.push({
            label: "会場名",
            value: formatTemplateMultilineValue(
              this.screeningInfo && this.screeningInfo.location
            ),
            withoutAppendingNewline: true,
          });
          templateObj.additionalReplacements.push({
            label: "会場住所",
            value: formatTemplateMultilineValue(
              this.screeningInfo && this.screeningInfo.address
            ),
            withoutAppendingNewline: true,
          });
          templateObj.additionalReplacements.push({
            label: "会場最寄駅",
            value: formatTemplateMultilineValue(
              this.screeningInfo && this.screeningInfo.nearestStation
            ),
            withoutAppendingNewline: true,
          });
          templateObj.additionalReplacements.push({
            label: "所要時間",
            value: formatTemplateMultilineValue(
              this.screeningInfo && this.screeningInfo.duration
            ),
            withoutAppendingNewline: true,
          });
          templateObj.additionalReplacements.push({
            label: "持参物",
            value: formatTemplateMultilineValue(
              this.screeningInfo && this.screeningInfo.preparation
            ),
            withoutAppendingNewline: true,
          });
          templateObj.additionalReplacements.push({
            label: "緊急連絡先",
            value: formatTemplateMultilineValue(
              this.screeningInfo && this.screeningInfo.emergencyContact
            ),
            withoutAppendingNewline: true,
          });
          templateObj.additionalReplacements.push({
            label: "対応事項1",
            value: (this.model.request1 && this.screeningInfo.request1) || "",
            withoutAppendingNewline: true,
          });
          templateObj.additionalReplacements.push({
            label: '対応期限1',
            value: this.buildActionDeadlineReplacement({
              requestNo: ProgressScreeningRequestStatusRequestNoEnum.actionItem1,
              requestEnabled: !!this.model.request1,
              requestText: this.screeningInfo && this.screeningInfo.request1,
              requestType: this.jobScreeningInfo && this.jobScreeningInfo.type,
              subStatusId: progressSubScreeningStatusId,
              correspondence,
              statuses,
            }),
            withoutAppendingNewline: true,
          });
          templateObj.additionalReplacements.push({
            label: "対応事項2",
            value: (this.model.request2 && this.screeningInfo.request2) || "",
            withoutAppendingNewline: true,
          });
          templateObj.additionalReplacements.push({
            label: '対応期限2',
            value: this.buildActionDeadlineReplacement({
              requestNo: ProgressScreeningRequestStatusRequestNoEnum.actionItem2,
              requestEnabled: !!this.model.request2,
              requestText: this.screeningInfo && this.screeningInfo.request2,
              requestType: this.jobScreeningInfo && this.jobScreeningInfo.type,
              subStatusId: progressSubScreeningStatusId,
              correspondence,
              statuses,
            }),
            withoutAppendingNewline: true,
          });
          templateObj.additionalReplacements.push({
            label: "対応事項3",
            value: (this.model.request3 && this.screeningInfo.request3) || "",
            withoutAppendingNewline: true,
          });
          templateObj.additionalReplacements.push({
            label: '対応期限3',
            value: this.buildActionDeadlineReplacement({
              requestNo: ProgressScreeningRequestStatusRequestNoEnum.actionItem3,
              requestEnabled: !!this.model.request3,
              requestText: this.screeningInfo && this.screeningInfo.request3,
              requestType: this.jobScreeningInfo && this.jobScreeningInfo.type,
              subStatusId: progressSubScreeningStatusId,
              correspondence,
              statuses,
            }),
            withoutAppendingNewline: true,
          });
          templateObj.additionalReplacements.push({
            label: "連絡事項",
            value: (this.message || "").trim()
          });
          templateObj.additionalReplacements.push({
            label: "日程調整テキスト",
            value: this.formatPossibleDate()
          });
          templateObj.additionalReplacements.push({
            label: "サブステータス-選考名",
            withoutAppendingNewline: true,
            value: (this.jobScreeningInfo.type || "")
              .replace(/[\r\n]+/g, "")
              .trim(),
          });
          this.confirmedMailTemplateTypeId = template.mailTemplateTypeId;
          return this.mailApiService.replaceTemplate(
            template.mailTemplateTypeId,
            templateObj
          );
        })
      )
      .subscribe(
        (replacedMailTemplate: Partial<MailTemplateResponse>) => {
          this.model.subject = replacedMailTemplate.subject;
          this.model.body = replacedMailTemplate.body;
          this.inConfirm = true;
          this.isTemplateLoadInProgress = false;
          this.mailTemplateSubscription.unsubscribe();
        },
        () => this.cancelMailTemplate()
      );
  }


  cancelMailTemplate(): void {
    if (this.mailTemplateSubscription) {
      this.mailTemplateSubscription.unsubscribe();
      this.mailTemplateSubscription = null;
    }
    this.inConfirm = false;
    this.isTemplateLoadInProgress = false;
  }

  send(): void {
    if (this.isSendInProgress) {
      return;
    }

    const screeningRequests = [];
    [1, 2, 3].forEach(i => {
      if (this.screeningInfo && this.screeningInfo['request' + i]) {
        screeningRequests.push({
          requestType: this.jobScreeningInfo.type,
          requestNo: i,
          isChecked: !!this.model['request' + i],
          manualDeadline: this.model['request' + i + 'DeadlineDate'] || null,
          jobScreeningInfoId: this.jobScreeningInfo.id
        });
      }
    });

    const progressIds =
      this.config.data.progressIds ||
      (this.config.data.progress
        ? [this.config.data.progress.id]
        : this.config.data.progressSubScreeningStatus
        ? [this.config.data.progressSubScreeningStatus.progressId]
        : []);

    if(!progressIds || progressIds.length === 0) {
      throw new Error('progressIds are invalid');
    }

    const bulkSettingInterviewRequest: ProgressBulkSettingInterviewRequest = {
      progressIds,
      dynamicData: {},
      screeningRequests: screeningRequests
    };

    if (this.possibleDate) {
      bulkSettingInterviewRequest.dynamicData.possibleDate = this.possibleDate;
    }
    if (this.message && this.message.trim()) {
      bulkSettingInterviewRequest.dynamicData.contactFromAdvisor = this.message.trim();
    }
    const correspondence = this.generateCorrespondenceText();

    if (correspondence) {
      bulkSettingInterviewRequest.dynamicData.correspondence = correspondence;
    }

    if (this.scheduleRadio === 1) {
      const recipientType =
        this.notifyRadio === EMAIL_NOTIFICATION_RECIPIENT_TYPE.ENTERPRISE
          ? EMAIL_NOTIFICATION_RECIPIENT_TYPE.ENTERPRISE
          : EMAIL_NOTIFICATION_RECIPIENT_TYPE.BIC;

      const to: string[] = [];
      const cc: string[] = [];

      this.contactSelections.to.forEach((selected, idx) => {
        if (selected) {
          const c = this.contacts[idx];
          if (c) {
            if (
              recipientType === EMAIL_NOTIFICATION_RECIPIENT_TYPE.ENTERPRISE
            ) {
              to.push(c.email);
            } else {
              to.push(String(c.id));
            }
          }
        }
      });

      this.contactSelections.cc.forEach((selected, idx) => {
        if (selected) {
          const c = this.contacts[idx];
          if (c) {
            if (
              recipientType === EMAIL_NOTIFICATION_RECIPIENT_TYPE.ENTERPRISE
            ) {
              cc.push(c.email);
            } else {
              cc.push(String(c.id));
            }
          }
        }
      });

      bulkSettingInterviewRequest.dynamicData.emailNotificationRecipient = {
        type: recipientType,
        to: to.join(","),
        cc: cc.join(","),
        ats:
          recipientType === EMAIL_NOTIFICATION_RECIPIENT_TYPE.ENTERPRISE &&
          this.atsAddressChecked &&
          this.atsAddress
            ? this.atsAddress
            : ""
      };
    }

    const bulkMailRequest: StudentMailBulkSendRequest = {
      userId: this.authService.loginUser.id,
      subject: this.model.subject,
      text: this.model.body,
      from: this.authService.loginUser.email,
      to: this.model.toEmails,
      cc: this.model.cc,
      jobId: this.config.data.jobId,
      templateTypeId: this.confirmedMailTemplateTypeId
    };

    const request = this.model.toEmails.length
      ? this.progressApiService
          .bulkSettingSubScreening(bulkSettingInterviewRequest)
          .pipe(
            concatMap(() =>
              this.mailApiService.sendStudentBulkMail(bulkMailRequest)
            )
          )
      : this.progressApiService.bulkSettingSubScreening(
          bulkSettingInterviewRequest
        );
    this.isSendInProgress = true;

    /* node 22 対応 */
    this.doMailSubscribe(request);

    setTimeout(() => {
      this.isSendInProgress = false;
      this.cancelSend();
      this.close(true);
      PopupControlComponent.instance.open({
        title: "メール送信処理開始",
        content:
          "メール送信処理を開始しました。\nしばらく経ってからメール管理画面へ反映されます。\n\nもしメール送信が失敗した場合、メール送信失敗メールが届きます。",
        confirmText: "OK",
        confirmCallback: () => false
      });
      CacheInterceptor.requestCountChange.emit(0);
    }, 10);

    setTimeout(() => {
      CacheInterceptor.requestCountChange.emit(0);
      this.isSendInProgress = false;
    }, 1000);
  }

  public doMailSubscribe(doMail: any): void {
    doMail.subscribe();
  }

  cancelSend(): void {
    if (this.sendSubscription) {
      this.sendSubscription.unsubscribe();
      this.sendSubscription = null;
    }
  }

  close(param?: any) {
    this.dialog.close(param);
  }

  // Eメールアドレス形式チェック
  private isEmail(email: string): boolean {
    if (!email || typeof email !== "string") return false;
    const re = /^(?!\.)(?!.*\.\.)[A-Za-z0-9._%+-]+(?<!\.)@(?:(?!-)[A-Za-z0-9-]{1,63}(?<!-)\.)+[A-Za-z]{2,}$/;
    return re.test(email.trim());
  }

  // 日程調整テキスト整形
  private formatPossibleDate(): string {
    const possibleDate =
      this.scheduleText &&
      this.scheduleText
        .split("\n")
        .filter(v => v && v.trim())
        .map(v => v.trim())
        .join("\n");

    this.possibleDate = possibleDate.trim() || "";
    return this.possibleDate;
  }

  getDeadlineDisplay(value: any) {
    if (value === null || value === undefined) {
      return null;
    }

    const numValue = Number(value);
    const option = SCREENING_INFO_REQUEST_DEADLINE_OPTIONS.find(o => o.value === numValue);

    if (!option || option.value === null) {
      return null;
    }

    const isManual = numValue === 999;
    return {
      isManual: isManual,
      label: option.label,
      subText: isManual ? "確定時の指定も可能です" : "日程確定時に日付が決まります"
    };
  }

  get subScreeningStatusLabel(): string {
    const status = this.config.data &&
      this.config.data.progressSubScreeningStatus &&
      this.config.data.progressSubScreeningStatus.status;
    if (status == null) {
      return "";
    }
    return PROGRESS_SUB_SCREENING_STATUS_CODE_LABEL_MAPPING[status] || "";
  }

  private getDeadlineTextForProgressDynamicData(value: any, manualDate?: string): string {
    if (value === null || value === undefined) {
      return null;
    }

    const numValue = Number(value);
    const isManual = numValue === 999;
    // 手動設定(999)かつ具体的な日付の入力がある場合、その日付を整形して返す
    if (isManual && manualDate) {
      const m = moment(manualDate).utcOffset(9).locale("ja");
      if (m.isValid()) {
        return `対応期限：${m.format("YYYY/MM/DD (ddd)")}`;
      }
    }

    const option = SCREENING_INFO_REQUEST_DEADLINE_OPTIONS.find(o => o.value === numValue);
    if (!option || option.value === null) {
      return null;
    }

    const label = isManual ? "未定" : option.label;
    const subText = isManual ? "今後設定される場合があります。お早めにご対応ください" : "日程確定時に日付が決まります";
    return `対応期限：${label}（${subText}）`
  }

  private generateCorrespondenceText(): string {
    if (!this.screeningInfo) {
      return "";
    }

    return [
      (this.model.request1 && this.screeningInfo.request1) || null,
      // 「requestDeadline = 0（当日）」を許可するためにnull/undefinedのみチェック
      (this.model.request1 && this.screeningInfo.request1Deadline != null &&
        this.getDeadlineTextForProgressDynamicData(this.screeningInfo.request1Deadline, this.model['request1DeadlineDate'])) || null,
      (this.model.request2 && this.screeningInfo.request2) || null,
      (this.model.request2 && this.screeningInfo.request2Deadline != null &&
        this.getDeadlineTextForProgressDynamicData(this.screeningInfo.request2Deadline, this.model['request2DeadlineDate'])) || null,
      (this.model.request3 && this.screeningInfo.request3) || null,
      (this.model.request3 && this.screeningInfo.request3Deadline != null &&
        this.getDeadlineTextForProgressDynamicData(this.screeningInfo.request3Deadline, this.model['request3DeadlineDate'])) || null,
    ]
      .filter(v => v !== null && v !== undefined && v !== '')
      .join("\n");
  }

  private buildActionDeadlineReplacement(params: {
    requestNo: ProgressScreeningRequestStatusRequestNoEnum;
    requestEnabled: boolean;
    requestText?: string;
    requestType?: string;
    subStatusId: number | null;
    correspondence: string;
    statuses: ProgressScreeningRequestStatus[];
  }): string {
    if (!params.requestEnabled || !params.requestText) {
      return "";
    }

    const statusRecord = this.findLatestRequestStatus(
      params.statuses,
      params.requestNo,
      params.requestType,
      params.subStatusId
    );

    const actionStatus =
      statusRecord && statusRecord.requestStatus === ProgressScreeningRequestStatusRequestStatusEnum.completed
        ? "対応済み"
        : "未対応";

    const actionLimit = (() => {
      if (statusRecord && statusRecord.requestDeadline != null && statusRecord.requestDeadline !== "") {
        const m = moment(statusRecord.requestDeadline).utcOffset(9).locale("ja");
        if (!m.isValid()) {
          return "";
        }
        return `(${m.format("YYYY/MM/DD (ddd)")})`;
      }

      return this.extractDeadlineFromCorrespondence(params.correspondence, params.requestText);
    })();

    return actionLimit ? `${actionStatus} ${actionLimit}` : actionStatus;
  }

  private findLatestRequestStatus(
    statuses: ProgressScreeningRequestStatus[],
    requestNo: ProgressScreeningRequestStatusRequestNoEnum,
    requestType?: string,
    subStatusId: number | null = null
  ): ProgressScreeningRequestStatus | null {
    const list = Array.isArray(statuses) ? statuses : [];
    const normalizedType = String(requestType || "").trim();

    const filtered = list.filter((s) => {
      const typeMatch = String(s.requestType || "").trim() === normalizedType;
      const subMatch = subStatusId == null
        ? s.progressSubScreeningStatusId == null
        : Number(s.progressSubScreeningStatusId) === Number(subStatusId);
      const noMatch = Number(s.requestNo) === Number(requestNo);
      return typeMatch && subMatch && noMatch;
    });

    if (!filtered.length) {
      return null;
    }

    return filtered.reduce<ProgressScreeningRequestStatus>((best, cur) => {
      const bestUpdatedAt = (best as any).updatedAt || (best as any).createdAt;
      const curUpdatedAt = (cur as any).updatedAt || (cur as any).createdAt;
      const bestTs = bestUpdatedAt ? new Date(bestUpdatedAt).getTime() : 0;
      const curTs = curUpdatedAt ? new Date(curUpdatedAt).getTime() : 0;

      if (curTs !== bestTs) {
        return curTs > bestTs ? cur : best;
      }

      const bestId = Number(best.id || 0);
      const curId = Number(cur.id || 0);
      return curId > bestId ? cur : best;
    }, filtered[0]);
  }

  private extractDeadlineFromCorrespondence(correspondence: string, requestText: string): string {
    if (!correspondence || !requestText) {
      return "";
    }

    const normalizedRequest = String(requestText).trim();
    const lines = String(correspondence).split(/\r?\n/);

    let lastRequestLine: string | null = null;
    for (const rawLine of lines) {
      const line = String(rawLine).trim();
      if (!line) continue;

      if (line.startsWith("対応期限：")) {
        if (lastRequestLine && lastRequestLine === normalizedRequest) {
          return line.replace(/^対応期限：\s*/, "").trim();
        }
        continue;
      }

      lastRequestLine = line;
    }

    return "";
  }
}
